import React, { useRef, useState } from 'react';
import { Camera, FileUp, Loader2 } from 'lucide-react';
import { uploadInvoiceFiles } from './api';

// What a supplier sends: PDF (printed or scanned), photos and screenshots (incl. iPhone HEIC), Word, and the
// spreadsheets some send. The server decides what each file really is from its bytes; this list only stops
// obviously wrong picks early. It matches the server's allow-list (backend/upload_guard.py INVOICE_ALLOW).
export const ACCEPT = [
  '.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff', '.webp', '.heic', '.heif', '.docx', '.csv', '.xlsx',
  'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/webp', 'image/heic', 'image/heif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');
const ALLOWED = /\.(pdf|jpe?g|png|tiff?|webp|hei[cf]|docx|csv|xlsx)$/i;
const OLD_WORD = /\.doc$/i;

export const REJECTED_MESSAGE = 'Only PDF, photo (JPG, PNG, HEIC, TIFF, WebP), Word (.docx) or spreadsheet files can be read';
export const OLD_WORD_MESSAGE = 'Old Word (.doc) files cannot be read: save it as .docx or PDF and upload that';

export function splitAllowed(files) {
  const ok = [];
  const rejected = [];
  for (const f of files) (ALLOWED.test(f.name || '') ? ok : rejected).push(f);
  return { ok, rejected };
}

/** Map the bulk response back onto the files sent: created docs by filename, the rest are duplicates. */
export function uploadsFromResponse(files, body, startedAt) {
  const docs = [...(body?.documents || [])];
  return files.map((f, i) => {
    const di = docs.findIndex((d) => (d.filename || d.original_filename) === f.name);
    const doc = di >= 0 ? docs.splice(di, 1)[0] : null;
    return { key: `${startedAt}-${i}-${f.name}`, name: f.name, startedAt, documentId: doc?.id || null, duplicate: !doc };
  });
}

export default function InvoiceUploader({ onUploads }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const send = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const startedAt = Date.now();
    const { ok, rejected } = splitAllowed(files);
    const bad = rejected.map((f, i) => ({ key: `${startedAt}-x${i}`, name: f.name, startedAt,
      error: OLD_WORD.test(f.name || '') ? OLD_WORD_MESSAGE : REJECTED_MESSAGE }));
    if (!ok.length) { onUploads(bad); return; }
    setBusy(true);
    try {
      const body = await uploadInvoiceFiles(ok);
      onUploads([...uploadsFromResponse(ok, body, startedAt), ...bad]);
    } catch (e) {
      onUploads([...ok.map((f, i) => ({ key: `${startedAt}-e${i}`, name: f.name, startedAt, error: e.message })), ...bad]);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  return (
    <section
      data-testid="invoice-uploader"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer?.files); }}
      className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 motion-organic ${drag ? 'border-primary-400 bg-primary-50' : 'border-surface-border bg-surface'}`}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1">
          <h2 className="font-display text-xl text-ink">Add supplier invoices</h2>
          <p className="text-sm text-ink-muted mt-1">
            Drop files here, or choose them: a PDF, a photo or screenshot (JPG, PNG, HEIC, TIFF, WebP) or a Word file. You can add several at once.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" data-testid="invoice-take-photo-btn" disabled={busy}
            onClick={() => cameraRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-50 motion-organic">
            <Camera className="w-5 h-5" /> Take photo
          </button>
          <button type="button" data-testid="invoice-choose-files-btn" disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 min-h-[52px] px-5 rounded-xl bg-primary-500 text-surface font-medium hover:bg-primary-600 disabled:opacity-50 motion-organic">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileUp className="w-5 h-5" />}
            {busy ? 'Uploading…' : 'Choose files'}
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" multiple accept={ACCEPT} className="sr-only" tabIndex={-1}
        aria-label="Choose invoice files" data-testid="invoice-file-input" onChange={(e) => send(e.target.files)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1}
        aria-label="Take a photo of an invoice" data-testid="invoice-camera-input" onChange={(e) => send(e.target.files)} />
    </section>
  );
}
